/**
 Author - Muhamed Afsal
 Git - https://bitbucket.org/mohdafsal/
 **/

import {useState, useEffect, useLayoutEffect, useRef} from "react";
import {useRouter} from "next/router";

import {WealthyEnv} from "helpers";

const visibilityType = {
  visible: "visible"
};

const usePopUpVisibility = (timeout) => {
  const [loginTime] = useState(new Date().getTime());
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const [isPopupShown, setPopupShown] = useState(false);
  const [show, setShow] = useState(false);


  useEffect(() => {
    if (!isPopupShown) {
      timeoutHandler();
      pageVisibiltyHandler();
    }

    /* cleans up every time component re-renders or unmounts */
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [currentTime, isPopupShown]);

  const timeoutHandler = () => {
    if (currentTime - loginTime > timeout) {
      setShow(true);
      setPopupShown(true);
    } else {
      setTimeout(() => {
        setCurrentTime(new Date().getTime());
      }, 1000);
    }
  };

  const pageVisibiltyHandler = () => {
    document.addEventListener("visibilitychange", handleVisibility);
  };

  const handleVisibility = () => {
    if (document.visibilityState === visibilityType.visible) {
      setShow(true);
      setPopupShown(true);
    }
  };

  return [show, setShow];
};

const useCheckMobileView = () => {
  const [isMobileView, setIsMobileView] = useState();

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobileView(isMobile);
  }, []);
  return isMobileView;
};

const useImpetusDesktopMeeting = () => {
  const [isDesktopMeeting, setIsDesktopMeeting] = useState(false);

  useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 769px)");
    const sync = () => setIsDesktopMeeting(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return isDesktopMeeting;
};

const useOnClickOutside = (ref, clickHandler) => {
  useEffect(() => {
    document.addEventListener("mousedown", checkClickOutside);

    return () => {
      document.removeEventListener("mousedown", checkClickOutside);
    };
  }, [ref, clickHandler]);

  const checkClickOutside = (event) => {
    if (ref && ref.current && !ref.current.contains(event.target)) {
      clickHandler();
    }
  };
};

const useScript = (scriptUrl) => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.type = "text/javascript";

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [scriptUrl]);
};

const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
};

const useCheckIsPartnerApp = () => {
  const [isPartnerApp, setIsPartnerApp] = useState();

  useEffect(() => {
    const isPartnerApp = window.location.pathname === "/become-wealth-partner";
    setIsPartnerApp(isPartnerApp);
  }, []);
  return isPartnerApp;
};

const useCurrentPageUrl = () => {
  const router = useRouter();
  let currentUrl = router.asPath;
  currentUrl = WealthyEnv.URL + currentUrl;

  return currentUrl;
};

/**
 * Loads more when the user scrolls near the bottom of the page.
 * Also checks once on mount (covers short pages that don't scroll).
 * @param {Function} loadMore - async () => void
 * @param {boolean} [enabled=true] - turn off when nothing left to load
 */
const useInfiniteScroll = (loadMore, enabled = true) => {
  const [loading, setLoading] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const tryLoadMore = async () => {
      if (busyRef.current) return;

      const nearBottom =
        document.documentElement.scrollHeight - 300 <
        window.scrollY + window.innerHeight;

      if (!nearBottom) return;

      busyRef.current = true;
      setLoading(true);
      try {
        await loadMore();
      } finally {
        busyRef.current = false;
        setLoading(false);
      }
    };

    let timeoutId;
    const onScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(tryLoadMore, 200);
    };

    // Short lists may not need a scroll gesture to be "at the bottom"
    tryLoadMore();

    window.addEventListener("scroll", onScroll, {passive: true});
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled, loadMore]);

  return {loading};
};

export {
  usePopUpVisibility,
  useOnClickOutside,
  useDebounce,
  useScript,
  useCheckMobileView,
  useImpetusDesktopMeeting,
  useCheckIsPartnerApp,
  useCurrentPageUrl,
  useInfiniteScroll
};
